import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";

/**
 * RoleRoute — generic role-gated wrapper.
 *
 * Props:
 *   - children: the protected element tree
 *   - allow: "sp" → only `service_provider` role can enter.
 *            "non-sp" → block `service_provider` users (used on chair/owner/admin routes).
 *
 * Always requires auth first (mirrors ProtectedRoute). Unknown roles are deny-by-default
 * for "sp" routes and allow-by-default for "non-sp" routes.
 */
export default function RoleRoute({ allow, children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        data-testid="role-route-loading"
      >
        <div className="flex items-center gap-2" style={{ color: "var(--slate)" }}>
          <span className="ai-dot" />
          <span className="ai-dot" />
          <span className="ai-dot" />
        </div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  const isSP = user.system_role === "service_provider";

  if (allow === "sp" && !isSP) {
    // Non-SP trying to access /sp/* — bounce to regular dashboard.
    return <Navigate to="/dashboard" replace />;
  }
  if (allow === "non-sp" && isSP) {
    // SP trying to access chair/owner/admin surfaces — bounce home.
    return <Navigate to="/sp/dashboard" replace />;
  }

  return children;
}
